import React from 'react';
import { Check, X, ChevronDown } from 'lucide-react';

const inputCx = 'w-full px-3 py-2 rounded-xl bg-white border border-slate-200 text-[13px] focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100';

/**
 * BulkEditModal — shared modal for bulk-editing service jobs.
 * `fields` describes editable fields: [{ key, label, options? }]
 *   - If options provided, renders a styled <select> with a visible chevron.
 *   - Otherwise renders a free-text input.
 * Calls `onApply(updatesObject)` with only the non-empty fields.
 */
export function BulkEditModal({
  open, onClose, count, onApply,
  fields = [
    { key: 'doc_type', label: 'Status' },
    { key: 'supervisor', label: 'Supervisor' },
    { key: 'technician', label: 'Technician' },
  ],
}) {
  const [updates, setUpdates] = React.useState({});

  React.useEffect(() => {
    if (!open) setUpdates({});
  }, [open]);

  if (!open) return null;

  const apply = () => {
    const filtered = Object.fromEntries(Object.entries(updates).filter(([, v]) => v));
    onApply(filtered);
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl border border-white/50 w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" data-testid="bulk-edit-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-[16px] font-semibold text-slate-900">Bulk Edit {count} Jobs</h3>
            <p className="text-[12.5px] text-slate-500">Leave a field blank to keep its current value.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X size={16} /></button>
        </div>
        <div className="space-y-3">
          {fields.map((f) => {
            const hasOptions = Array.isArray(f.options) && f.options.length > 0;
            return (
              <div key={f.key}>
                <label className="block text-[12px] font-medium text-slate-600 mb-1">{f.label}</label>
                {hasOptions ? (
                  <div className="relative">
                    <select
                      value={updates[f.key] || ''}
                      onChange={(e) => setUpdates((u) => ({ ...u, [f.key]: e.target.value }))}
                      className={`${inputCx} appearance-none pr-9 cursor-pointer`}
                      data-testid={`bulk-${f.key}`}
                    >
                      <option value="">— No change —</option>
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <ChevronDown size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                ) : (
                  <input
                    value={updates[f.key] || ''}
                    onChange={(e) => setUpdates((u) => ({ ...u, [f.key]: e.target.value }))}
                    className={inputCx}
                    placeholder="— No change —"
                    data-testid={`bulk-${f.key}`}
                  />
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 text-[13px]">Cancel</button>
          <button
            onClick={apply}
            data-testid="bulk-edit-apply"
            className="px-4 py-2 rounded-xl text-white text-[13px] font-medium flex items-center gap-2"
            style={{ background: 'linear-gradient(135deg, #0EA5E9 0%, #6366F1 60%, #DB2777 100%)' }}
          >
            <Check size={14} /> Apply
          </button>
        </div>
      </div>
    </div>
  );
}

export default BulkEditModal;
